import InfoIcon from '@mui/icons-material/Info';
import { Grid, IconButton, Popover, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getStainTypeFilteredIds } from 'api/stainTypes';
import {
  defaultDeconvolutionStainVector,
  defaultDeconvolutionStainVectorHematoxylin,
} from 'components/StudiesDashboard/StudySettings/DeconvolutionSettings/DeconvolutionCalculationSettings';
import { getStainingMethodByStainType, HEMATOXYLIN_STAIN_ID, StainingMethod } from 'interfaces/stainType';
import { CalculationSettingsMultiStain, CalculationSettingsSingleStain } from 'interfaces/study';
import { first, forEach, get, includes, isEmpty, keys, map, split } from 'lodash';
import { default as React, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { CasesParams } from 'utils/useCasesParams/types';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import useStudy from 'utils/useStudy';
import { ChannelsController } from '../ChannelsController';
import { QuantificationFormValues } from './types';

export interface AdditionalParametersStepProps {
  casesParams: CasesParams;
  methods: UseFormReturn<QuantificationFormValues, any>;
  checkValidationAndSetIsStepFailed: () => void;
}

export const AdditionalParametersStep: React.FC<AdditionalParametersStepProps> = ({
  casesParams,
  methods,
  checkValidationAndSetIsStepFailed,
}) => {
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { data: study, isFetched: isStudyFetched } = useStudy(casesParams.filters?.studyId);

  const { data: stains, isFetched: isStainsFetched } = useQuery({
    queryKey: ['slidesStainsTypes', casesParams],
    queryFn: ({ signal }) => getStainTypeFilteredIds(casesParams, signal),
  });

  const oneRelevantStain = first(stains);
  const stainsForVectorStains = includes(oneRelevantStain, '_') ? split(oneRelevantStain, '_') : [oneRelevantStain];
  const stainingMethod = getStainingMethodByStainType(oneRelevantStain);

  useEffect(() => {
    if (isStainsFetched && isStudyFetched) {
      if (stainingMethod === StainingMethod.ihc || stainingMethod === StainingMethod.chromogenicMplex) {
        const stainVectors: { [key: string]: number[] } = {};
        stainVectors[HEMATOXYLIN_STAIN_ID] = defaultDeconvolutionStainVectorHematoxylin;

        forEach(stainsForVectorStains, (stain) => {
          stainVectors[stain] = defaultDeconvolutionStainVector;
        });

        if (!isEmpty(study?.settings?.deconvolutionSettings?.calculationSettings)) {
          // If the study has deconvolution settings, we can use them to set the stain vectors
          const deconvolutionSettings = study?.settings?.deconvolutionSettings?.calculationSettings;
          if (deconvolutionSettings[oneRelevantStain]) {
            if (stainingMethod === StainingMethod.ihc) {
              stainVectors[oneRelevantStain] = (deconvolutionSettings as CalculationSettingsSingleStain)[
                oneRelevantStain
              ];
            } else if (stainingMethod === StainingMethod.chromogenicMplex) {
              forEach(
                keys((deconvolutionSettings as CalculationSettingsMultiStain)[oneRelevantStain]),
                (stainSetting) => {
                  stainVectors[stainSetting] = (deconvolutionSettings as CalculationSettingsMultiStain)[
                    oneRelevantStain
                  ][stainSetting];
                }
              );
            }
          }

          // check if hematoxylin is in the deconvolution settings
          if (deconvolutionSettings[HEMATOXYLIN_STAIN_ID]) {
            stainVectors[HEMATOXYLIN_STAIN_ID] = (deconvolutionSettings as CalculationSettingsSingleStain)[
              HEMATOXYLIN_STAIN_ID
            ];
          }
        }

        setValue('configParams.fixedStainParams', {
          stainVectors: stainVectors,
        });
      }
    }
  }, [isStainsFetched, isStudyFetched]);

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <ChannelsController
          name="configParams.quantifiedChannelNames"
          error={Boolean(get(errors, 'configParams.quantifiedChannelNames'))}
          helperText={get(errors, 'configParams.quantifiedChannelNames')?.message}
          methods={methods}
          additionalOnChange={() => {
            checkValidationAndSetIsStepFailed();
          }}
          casesParams={casesParams}
        ></ChannelsController>
      </Grid>
      {stainingMethod !== StainingMethod.mplex && (
        <Grid item container>
          <Grid item container xs={12} spacing={1} alignItems="center">
            <Grid item>
              <Typography variant="h4">Stain Vectors</Typography>
            </Grid>
            <Grid item>
              <IconButton onClick={handleClick}>
                <InfoIcon fontSize="small" />
              </IconButton>
              <Popover onClick={(e) => e.stopPropagation()} open={open} anchorEl={anchorEl} onClose={handleClose}>
                <Typography variant="body2" sx={{ padding: 2 }}>
                  The stain vectors are declared in the study settings.
                </Typography>
              </Popover>
            </Grid>
          </Grid>
          {map(watch('configParams.fixedStainParams.stainVectors'), (stainVector, stainTypeId) => {
            return (
              <Grid item xs={12} key={stainTypeId}>
                <Grid container direction="row" alignItems="center" spacing={2}>
                  <Grid item xs={2}>
                    <Typography variant="subtitle1">{stainTypeIdToDisplayName(stainTypeId)}</Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <Typography variant="subtitle2">{JSON.stringify(stainVector)}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Grid>
  );
};
